import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export interface Notification {
  id: string
  recipient_id: string
  sender_id: string
  type: string
  title: string
  content: string | null
  related_id: string | null
  action_url: string | null
  read: boolean
  read_at: string | null
  created_at: string
  sender?: {
    id: string
    username: string
    display_name: string | null
    avatar_url: string | null
    status: string
  }
}

export function useNotifications(userId: string) {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [unreadCount, setUnreadCount] = useState(0)

  // Load notifications on mount
  useEffect(() => {
    if (userId) {
      loadNotifications()
    }
  }, [userId])

  // Real-time subscription for new notifications
  useEffect(() => {
    if (!userId) return

    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${userId}`
        },
        async (payload) => {
          // Fetch the sender's profile for the new notification
          const { data: sender } = await supabase
            .from('profiles')
            .select('id, username, display_name, avatar_url, status')
            .eq('id', payload.new.sender_id)
            .single()

          const newNotification = {
            ...payload.new,
            sender
          } as Notification

          setNotifications(prev => [newNotification, ...prev])
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${userId}`
        },
        (payload) => {
          // Update notification in list (e.g., when marked as read)
          setNotifications(prev =>
            prev.map(n => n.id === payload.new.id ? { ...n, ...payload.new } : n)
          )
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [userId])

  // Update unread count whenever notifications change
  useEffect(() => {
    const count = notifications.filter(n => !n.read).length
    setUnreadCount(count)
  }, [notifications])

  // Load notifications from database
  const loadNotifications = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_id', userId)
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) {
        console.error('Error loading notifications:', error)
        return
      }

      setNotifications(data || [])
    } catch (err) {
      console.error('Error loading notifications:', err)
    } finally {
      setLoading(false)
    }
  }

  // Mark a single notification as read
  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({
          read: true,
          read_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('recipient_id', userId) // Security: ensure user owns notification

      if (error) {
        console.error('Error marking notification as read:', error)
        return
      }

      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n.id === id
            ? { ...n, read: true, read_at: new Date().toISOString() }
            : n
        )
      )
    } catch (err) {
      console.error('Error marking notification as read:', err)
    }
  }

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const unreadIds = notifications.filter(n => !n.read).map(n => n.id)

      if (unreadIds.length === 0) return

      const { error } = await supabase
        .from('notifications')
        .update({
          read: true,
          read_at: new Date().toISOString()
        })
        .in('id', unreadIds)
        .eq('recipient_id', userId) // Security: ensure user owns notifications

      if (error) {
        console.error('Error marking all notifications as read:', error)
        return
      }

      // Update local state
      const now = new Date().toISOString()
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true, read_at: now }))
      )
    } catch (err) {
      console.error('Error marking all notifications as read:', err)
    }
  }

  return {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    refresh: loadNotifications
  }
}
